import React, { useState, useCallback, useMemo } from "react";
import Dropdown from "./dropdown";
import { FaCaretRight, FaCaretDown } from "react-icons/fa";
import { TreeSelectDropdownProps } from "./types";
import Toggle from "../toggle";
import { TreeNodeType } from "./types";

const TreeSelectDropdown = <NodeType extends TreeNodeType<NodeType>>({
    label,
    options = [],
    selectedIds = [],
    onOptionToggle,
    isLoading = false,
    labelClassName = "",
    dropdownClassName = "",
    enableSearch = true,
    initiallyExpanded = false,
    showHideUnselectedToggle = false,
    renderNode
}: TreeSelectDropdownProps<NodeType>) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
    const [hideUnselected, setHideUnselected] = useState(false);

    // Recursive function to filter the tree
    const filterTree = useCallback((node: NodeType, term: string): NodeType | null => {
        if (hideUnselected && !selectedIds.includes(node.public_id) && !(node.children ?? []).some(child => selectedIds.includes(child.public_id))) {
            return null;
        }
        if (node.name.trim().toLowerCase().includes(term.trim().toLowerCase())) {
            return node;
        }
        const filteredChildren = (node.children ?? [])
            .map(child => filterTree(child, term))
            .filter((child): child is NodeType => child !== null);
        if (filteredChildren.length > 0) {
            return { ...node, children: filteredChildren };
        }
        return null;
    }, [hideUnselected, selectedIds]);

    // Apply filtering to the options
    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;

        return options
            .map(node => filterTree(node, searchTerm))
            .filter((node): node is NodeType => node !== null);
    }, [options, searchTerm, filterTree]);

    // Recursively collects all node IDs, including those of children
    const getAllNodeIds = useCallback((nodes: NodeType[] = []): string[] => {
        const ids: string[] = [];
        const traverse = (nodeArray: NodeType[]) => {
            if (!Array.isArray(nodeArray)) return;
            for (const node of nodeArray) {
                if (node && typeof node === 'object') {
                    ids.push(node.public_id);
                    if (Array.isArray(node.children) && node.children.length > 0) {
                        traverse(node.children);
                    }
                }
            }
        };
        traverse(nodes);
        return ids;
    }, []);

    // Memoized value containing all node IDs based on the current options
    const allNodeIds = useMemo(() => getAllNodeIds(options || []), [options, getAllNodeIds]);

    // Memoized value to determine which nodes should be expanded based on search term or initial expansion state
    const effectiveExpandedNodes = useMemo(() => {
        if (searchTerm || (initiallyExpanded && expandedNodes.length === 0)) {
            return allNodeIds;
        }
        return expandedNodes;
    }, [searchTerm, initiallyExpanded, expandedNodes, allNodeIds]);

    // Toggles the expansion state of a node
    const toggleNodeExpansion = useCallback((nodeId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        setExpandedNodes(prev => {
            const currentExpanded = prev.length === 0 && initiallyExpanded ? allNodeIds : prev;
            return currentExpanded.includes(nodeId)
                ? currentExpanded.filter(id => id !== nodeId)
                : [...currentExpanded, nodeId];
        });
    }, [initiallyExpanded, allNodeIds]);

    // Collects all children IDs for a given node recursively
    const getAllChildrenIds = useCallback((node: NodeType): string[] => {
        return [node.public_id, ...(Array.isArray(node.children) ? node.children.flatMap(getAllChildrenIds) : [])];
    }, []);

    const handleSelectionChange = useCallback((newSelectedIds: string[]) => {
        const currentSelectedIds = selectedIds;
        const added = newSelectedIds.filter(id => !currentSelectedIds.includes(id));
        const removed = currentSelectedIds.filter(id => !newSelectedIds.includes(id));

        if (added.length > 0) {
            onOptionToggle(added);
        } else if (removed.length > 0) {
            onOptionToggle(removed);
        }
    }, [selectedIds, onOptionToggle]);

    // Toggle the selection state of a node and its children
    const handleNodeToggle = useCallback((node: NodeType) => {
        const childrenIds = getAllChildrenIds(node);
        const isSelected = selectedIds.includes(node.public_id);
        const updatedSelections = isSelected
            ? selectedIds.filter(id => !childrenIds.includes(id))
            : Array.from(new Set([...selectedIds, ...childrenIds]));

        handleSelectionChange(updatedSelections);
    }, [selectedIds, getAllChildrenIds, handleSelectionChange]);

    const isNodeSelected = useCallback((node: NodeType): boolean => {
        return selectedIds.includes(node.public_id);
    }, [selectedIds]);

    // Checks if any children of a node are selected for partial selection state rendering
    const hasSelectedChildren = useCallback((node: NodeType): boolean => {
        if (!Array.isArray(node.children)) return false;
        for (const child of node.children) {
            if (selectedIds.includes(child.public_id) || hasSelectedChildren(child)) {
                return true;
            }
        }
        return false;
    }, [selectedIds]);

    // Renders either a caret icon for expandable nodes or a placeholder for non-expandable nodes
    const renderCaretOrPlaceholder = useCallback((node: NodeType, isExpanded: boolean) => {
        return Array.isArray(node.children) && node.children.length > 0 ? (
            <span
                className="mr-2 w-4 inline-block"
                onClick={(e) => toggleNodeExpansion(node.public_id, e)}
                aria-hidden="true"
            >
                {isExpanded ? (
                    <FaCaretDown className="fa-fw" />
                ) : (
                    <FaCaretRight className="fa-fw" />
                )}
            </span>
        ) : (
            <span className="mr-2 w-4 inline-block"></span>
        );
    }, [toggleNodeExpansion]);

    // Recursive function to render the tree nodes and their children
    const renderTreeNodes = useCallback((nodes: NodeType[] = [], depth = 0) => {
        return Array.isArray(nodes) ? nodes.map((node) => {
            const filteredNode = filterTree(node, searchTerm);
            if (!filteredNode) return null;

            const isSelected = isNodeSelected(node);
            const hasSelectedDescendants = hasSelectedChildren(node);
            const isExpanded = effectiveExpandedNodes.includes(node.public_id);

            return (
                <React.Fragment key={node.public_id}>
                    <div
                        role="treeitem"
                        aria-expanded={Array.isArray(node.children) && node.children.length > 0 ? isExpanded : undefined}
                        aria-selected={isSelected}
                        className="hover:bg-blue-300 hover:text-white cursor-pointer"
                        style={{ paddingLeft: `${depth * 25 + 16}px` }}
                        onClick={() => handleNodeToggle(node)}
                    >
                        <div className="flex items-center py-2">
                            <div className="flex-shrink-0 flex items-center">
                                {renderCaretOrPlaceholder(node, isExpanded)}
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleNodeToggle(node)}
                                    className={`form-checkbox mr-2 flex-shrink-0 ${hasSelectedDescendants && !isSelected ? 'bg-blue-50' : ''}`}
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label={`Select ${node.name}`}
                                />
                            </div>
                            {renderNode(node)}
                        </div>
                    </div>
                    {Array.isArray(node.children) && node.children.length > 0 && isExpanded && (
                        <div role="group">
                            {renderTreeNodes(node.children, depth + 1)}
                        </div>
                    )}
                </React.Fragment>
            );
        }).filter(Boolean) : null;
    }, [effectiveExpandedNodes, isNodeSelected, hasSelectedChildren, handleNodeToggle, renderCaretOrPlaceholder, filterTree, searchTerm]);

    const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setSearchTerm(newValue);
    };

    return (
        <Dropdown
            label={label}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            className={labelClassName}
        >
            <div role="tree" className={`max-h-[24rem] overflow-auto ${dropdownClassName}`}>
                <div className="px-4 py-2 items-center" onKeyDown={(e) => e.stopPropagation()}>
                    {enableSearch && (
                        <input
                            type="text"
                            className="form-input mt-1 block w-full rounded text-gray-700 px-3 py-2"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={handleSearchTermChange}
                        />
                    )}
                    {showHideUnselectedToggle && (
                        <div className="mt-3 mb-1 flex items-center justify-start">
                            <Toggle
                                value={hideUnselected}
                                onChange={setHideUnselected}
                                label={hideUnselected ? "Show all" : "Hide unselected"}
                                labelPosition="right"
                                size="small"
                                layout="horizontal"
                                labelClassName="text-xs text-gray-600"
                                className="flex-shrink-0"
                            />
                        </div>
                    )}
                </div>
                {isLoading ? (
                    <div className="px-4 py-2">Loading...</div>
                ) : filteredOptions.length === 0 ? (
                    <div className="px-4 py-2">No results found.</div>
                ) : (
                    renderTreeNodes(filteredOptions)
                )}
            </div>
        </Dropdown>
    );
};

export default TreeSelectDropdown;